import React, { useMemo, useState } from 'react'
import { Link, useForm } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'

function parseKeywords(input) {
  return input
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

export default function New() {
  const [activeTab, setActiveTab] = useState('ai')
  const [isGenerating, setIsGenerating] = useState(false)
  const [secondaryKeywordsInput, setSecondaryKeywordsInput] = useState('')

  const { data, setData, post, processing, errors } = useForm({
    primary_query: '',
    secondary_keywords: [],
    title: '',
    meta_title: '',
    meta_description: '',
    og_title: '',
    og_description: '',
    slug: '',
    outline: '',
    body_html: '',
    internal_links: [],
    thumbnail_url: ''
  })

  const internalLinksPreview = useMemo(() => {
    return Array.isArray(data.internal_links) ? data.internal_links : []
  }, [data.internal_links])

  async function handleGenerate() {
    if (!data.primary_query.trim()) {
      alert('Please enter a primary query.')
      return
    }

    setIsGenerating(true)
    try {
      const response = await fetch('/admin/blog_posts/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          primary_query: data.primary_query,
          secondary_keywords: parseKeywords(secondaryKeywordsInput)
        })
      })

      const contentType = response.headers.get('content-type') || ''
      const isJson = contentType.includes('application/json')

      if (!response.ok) {
        const bodyText = await response.text()
        throw new Error(`Generation failed (${response.status}). ${isJson ? '' : bodyText.slice(0, 200)}`)
      }

      if (!isJson) {
        const bodyText = await response.text()
        throw new Error(`Generation failed: expected JSON, got "${contentType}". ${bodyText.slice(0, 200)}`)
      }

      const result = await response.json()

      setData(prev => ({
        ...prev,
        secondary_keywords: parseKeywords(secondaryKeywordsInput),
        title: result.h1_title || '',
        meta_title: result.meta_title || '',
        meta_description: result.meta_description || '',
        og_title: result.og_title || '',
        og_description: result.og_description || '',
        slug: result.slug || '',
        outline: Array.isArray(result.outline) ? result.outline.join('\n') : (result.outline || ''),
        body_html: result.body_html || '',
        internal_links: result.internal_links || [],
        thumbnail_url: result.thumbnail_url || ''
      }))

      setActiveTab('manual')
    } catch (e) {
      console.error(e)
      alert(`Failed to generate. ${e?.message || 'Check console.'}`)
    } finally {
      setIsGenerating(false)
    }
  }

  function submit(e) {
    e.preventDefault()
    post('/admin/blog_posts')
  }

  return (
    <UserLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">New Blog Post</h1>
          <Link href="/admin/blog_posts" className="text-gray-600 hover:text-gray-900">Cancel</Link>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'ai'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('ai')}
          >
            ✨ AI Generator
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'manual'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('manual')}
          >
            Manual / Review
          </button>
        </div>

        {activeTab === 'ai' && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Primary query (full search query)</label>
              <input
                type="text"
                value={data.primary_query}
                onChange={e => setData('primary_query', e.target.value)}
                placeholder='e.g. "how to learn french fast"'
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                disabled={isGenerating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Secondary keywords (comma-separated)</label>
              <input
                type="text"
                value={secondaryKeywordsInput}
                onChange={e => setSecondaryKeywordsInput(e.target.value)}
                placeholder="e.g. vocabulary, immersion, reading practice"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                disabled={isGenerating}
              />
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !data.primary_query.trim()}
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {isGenerating ? 'Generating…' : 'Generate blog post + banner image'}
              </button>
            </div>

            <div className="text-xs text-gray-500">
              Generates: H1, meta/OG, slug, outline, full HTML body, internal link suggestions, and a banner image.
            </div>
          </div>
        )}

        {/* Manual form */}
        <form onSubmit={submit} className={`bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-6 ${activeTab === 'ai' ? 'opacity-50 pointer-events-none filter blur-[1px]' : ''}`}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Primary query</label>
            <input
              type="text"
              value={data.primary_query}
              onChange={e => setData('primary_query', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
            {errors.primary_query && <div className="text-red-500 text-sm mt-1">{errors.primary_query}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Secondary keywords</label>
            <input
              type="text"
              value={(Array.isArray(data.secondary_keywords) ? data.secondary_keywords : []).join(', ')}
              onChange={e => setData('secondary_keywords', parseKeywords(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Banner image</label>
            {data.thumbnail_url ? (
              <div className="space-y-3">
                <img src={data.thumbnail_url} alt="Banner" className="h-44 w-full object-cover rounded-md" />
                <div>
                  <button type="button" onClick={() => setData('thumbnail_url', '')} className="text-sm text-red-600 hover:text-red-800 underline">
                    Remove image
                  </button>
                </div>
              </div>
            ) : (
              <input
                type="text"
                placeholder="Paste image URL (optional)"
                value={data.thumbnail_url}
                onChange={e => setData('thumbnail_url', e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-sm"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">H1 / Title</label>
            <input
              type="text"
              value={data.title}
              onChange={e => setData('title', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
            {errors.title && <div className="text-red-500 text-sm mt-1">{errors.title}</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Meta title (≤ 60 chars)</label>
              <input
                type="text"
                value={data.meta_title}
                onChange={e => setData('meta_title', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <input
                type="text"
                value={data.slug}
                onChange={e => setData('slug', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono"
              />
              {errors.slug && <div className="text-red-500 text-sm mt-1">{errors.slug}</div>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Meta description (155–160 chars)</label>
            <textarea
              rows="2"
              value={data.meta_description}
              onChange={e => setData('meta_description', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">OG title</label>
              <input
                type="text"
                value={data.og_title}
                onChange={e => setData('og_title', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">OG description</label>
              <input
                type="text"
                value={data.og_description}
                onChange={e => setData('og_description', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Outline</label>
            <textarea
              rows="6"
              value={data.outline}
              onChange={e => setData('outline', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Body HTML</label>
            <textarea
              rows="16"
              value={data.body_html}
              onChange={e => setData('body_html', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono text-sm"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Internal link suggestions</h3>
              <span className="text-xs text-gray-500">{internalLinksPreview.length} items</span>
            </div>
            {internalLinksPreview.length > 0 ? (
              <ul className="mt-3 space-y-2 text-sm">
                {internalLinksPreview.map((l, i) => (
                  <li key={i} className="bg-white border rounded p-3">
                    <div className="font-medium text-gray-900">{l.anchor_text}</div>
                    <div className="text-gray-600 font-mono">{l.url}</div>
                    {l.context && <div className="text-gray-500 mt-1">{l.context}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-sm text-gray-500 italic">No suggestions yet.</div>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-2">
            <Link href="/admin/blog_posts" className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</Link>
            <button
              type="submit"
              disabled={processing}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {processing ? 'Creating…' : 'Create Draft'}
            </button>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}



